import { useEffect, useState, useCallback } from "react";
import NetworkStatusPill from "../networkStatus/NetworkStatusPill";
import { FaArrowLeft } from "react-icons/fa6"; // Import icons
import { FiAlignJustify } from "react-icons/fi";
import logo from "../../assets/logo.svg";
import logo1 from "../../assets/logo1.svg";
import tabIcon from "../../assets/tab-icon.svg";
import ResetPasswordModal from "../resetPassword";

import { Container, Dropdown, Nav, Navbar, Offcanvas } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { MobileFooter } from "../footer";
import { useDispatch, useSelector } from "react-redux";
import { performLogout } from "../../hooks/performLogout.js";
import { clearAllOrgDetails } from "../../store/orgDetailsSlice";
import { clearOrgList } from "../../store/orgListSlice";
import MessagePopup from "../messagePopup";
import { showLoader, hideLoader } from "../../store/loadingSlice";
import {
  selectUserInfo,
  selectIsAuthenticated,
  selectIsAIAgent,
  selectIsAdmin,
  selectIsEnsarasAdmin,
  setIsAIAgent,
  setIsLoggingOut,
  logout,
} from "../../store/authUserSlice.js";
import {
  useNetworkContext,
  useNetworkStatusChange,
} from "../../context/NetworkContext";
import useOfflineGuard from "../../hooks/useOfflineGuard";

function AppNavBar({
  needBack,
  setKey,
  activeKey,
  organization,
  backLink,
  resetTabEdits,
  setEditMode,
  isTabShown,
  onCustomBack,
}) {
  const [showSidebar, setShowSidebar] = useState(false); // State to control sidebar visibility
  const [isWideEnough, setIsWideEnough] = useState(window.innerWidth > 1000);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAIAgent = useSelector(selectIsAIAgent);
  const isAdmin = useSelector(selectIsAdmin);
  const isEnsarasAdmin = useSelector(selectIsEnsarasAdmin);
  const isOnline = useNetworkContext();

  const OFFLINE_WARNING =
    "You are offline. If you navigate away from this page, you may encounter a blank page until you are back online. Do you want to continue?";

  // Wrap any programmatic navigation with the offline check
  const handleNavWithGuard = useCallback(
    (navFn) => {
      if (!isOnline) {
        if (window.confirm(OFFLINE_WARNING)) navFn();
      } else {
        navFn();
      }
    },
    [isOnline],
  );

  // Block browser back + reload when offline
  useOfflineGuard(isOnline);
  useEffect(() => {
    const handleResize = () => {
      setIsWideEnough(window.innerWidth > 1000);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleTabChange = (key) => {
    if (typeof setKey === "function") {
      setKey(key);
    } else if (organization) {
      navigate(`/organization/${organization}?activeTab=${key}`);
    }
    if (typeof resetTabEdits === "function") {
      resetTabEdits();
    }
  };
  const handleLogout = async () => {
    dispatch(setIsLoggingOut(true));
    dispatch(showLoader());
    try {
      // Grafana logout, then OIDC RP-initiated logout (which redirects browser to post_logout_redirect_uri)
      await performLogout();
    } catch (error) {
      console.error("Failed to logout", error);
      dispatch(setIsLoggingOut(false));
      dispatch(hideLoader());
      // Fallback: clear Redux state and navigate to signin if signoutRedirect fails
      dispatch(logout());
      dispatch(clearOrgList());
      dispatch(clearAllOrgDetails());
      navigate("/signin");
    }
  };
  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const closeSidebar = () => {
    setShowSidebar(false);
  };

  // message window content
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const updateMessage = (type, content) => {
    setMessageType(type);
    setMessage(content);
  };

  // handle reset password modal open/close
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const handleResetPasswordModal = (status) => {
    setShowResetPasswordModal(status);
    if (localStorage.getItem("activeTab")) {
      resetTabEdits();
    } else {
      setEditMode(false);
    }
  };

  // change the layout by page width
  const [expand, setExpand] = useState(window.innerWidth > 1000);
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1000) {
        setExpand("sm");
      } else {
        setExpand(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useNetworkStatusChange((online) => {
    updateMessage(
      online ? "success" : "error",
      online
        ? "You are back online. Submitted entries will be synced automatically."
        : "You are offline. You can continue working on this form. Submitted entries will be queued and synced automatically when your connection returns.",
    );
  });

  if (isWideEnough) {
    return (
      <div className="navbar-wrapper">
        <MessagePopup
          message={message}
          messageType={messageType}
          onClose={() => setMessage("")}
        />
        <Navbar key={expand} expand={expand} className="navbar-wrapper">
          <Container fluid>
            {needBack && (
              <span
                onClick={(e) =>
                  handleNavWithGuard(() =>
                    onCustomBack ? onCustomBack(e) : navigate(backLink || "/"),
                  )
                }
                className="back-button"
                style={{ cursor: "pointer" }}
              >
                <FaArrowLeft /> Back
              </span>
            )}
            <div className="Navbar.Brand">
              <a
                href="/dashboard"
                className="logo"
                onClick={() => dispatch(setIsAIAgent(false))}
              >
                <img
                  src={logo}
                  alt="Ensaras Logo"
                  className="logo desk-logo"
                  loading="lazy"
                />
              </a>
            </div>
            <Navbar.Toggle aria-controls={`offcanvasNavbar-expand-${expand}`} />
            <Navbar.Offcanvas
              id={`offcanvasNavbar-expand-${expand}`}
              aria-labelledby={`offcanvasNavbarLabel-expand-${expand}`}
              placement="end"
            >
              <Offcanvas.Header closeButton>
                <Offcanvas.Title id={`offcanvasNavbarLabel-expand-${expand}`}>
                  Tools
                </Offcanvas.Title>
              </Offcanvas.Header>
              <Offcanvas.Body>
                <Nav
                  className="justify-content-end flex-grow-1 pe-3"
                  style={{ alignItems: "center", gap: "4px" }}
                >
                  <NetworkStatusPill />
                  {isAuthenticated && <div className="nav-pipe"></div>}
                  {isAuthenticated && (
                    <>
                      {isAIAgent && (
                        <button
                          onClick={() =>
                            navigate(`/ai-assistant/${organization}`)
                          }
                          className="nav-btn"
                        >
                          AI Assistant
                        </button>
                      )}
                      {organization && (
                        <button
                          onClick={() => navigate(`/documents/${organization}`)}
                          className={`nav-btn ${location.pathname.includes('/documents') ? 'active-nav-btn' : ''}`}
                        >
                          Documents
                        </button>
                      )}
                      <a
                        href="mailto:support@ensaras.com?subject=Incident:&body=%0AIncident%20Description:%0A%0A"
                        className="nav-btn"
                      >
                        Support
                      </a>
                      <button
                        className="nav-btn"
                        onClick={() => handleResetPasswordModal(true)}
                      >
                        Reset Password
                      </button>
                      <button onClick={handleLogout} className="nav-btn">
                        Log out
                      </button>
                    </>
                  )}
                </Nav>
              </Offcanvas.Body>
            </Navbar.Offcanvas>
          </Container>
        </Navbar>

        <ResetPasswordModal
          show={showResetPasswordModal}
          onHide={() => handleResetPasswordModal(false)}
          updateMessage={updateMessage}
        />
      </div>
    );
  } else {
    return (
      <div className="navbar-wrapper">
        <MessagePopup
          message={message}
          messageType={messageType}
          onClose={() => setMessage("")}
        />
        <Navbar key={expand} expand={expand} className="navbar-wrapper">
          <Container fluid>
            {!needBack && organization && (
              <div
                className={`mobile-menu mobile-tab ${isTabShown ? "show-menu" : ""
                  }`}
              >
                {/* userInfo.role has no Scope equivalent for the "Client User"
                    tier specifically (see docs/specs/0001-oidc-migration.md,
                    ticket 10) - the backend hasn't defined one, so role is
                    always null post-cutover and this clause is always true,
                    slightly more permissive than the pre-migration behavior
                    for that one tier. Flagged, not silently assumed fine;
                    revisit once/if the backend exposes a matching Scope. */}
                {((userInfo.role !== "Client User" &&
                  localStorage.getItem("orgLength") > 1) ||
                  isAdmin ||
                  isTabShown) && (
                    <Dropdown>
                      <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                        <img
                          src={tabIcon}
                          alt="Menu"
                          style={{ width: "24px", height: "24px", minWidth: "24px" }}
                          loading="lazy"
                        />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {/* <img
                        src={tabIcon}
                        alt="Menu"
                        style={{ width: "24px", height: "24px", marginLeft: "20px" }}
                      /> */}
                        <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                          <img
                            src={tabIcon}
                            alt="Menu"
                            style={{
                              width: "24px",
                              height: "24px",
                              marginLeft: "20px",
                              marginBottom: "20px",
                            }}
                            loading="lazy"
                          />
                        </Dropdown.Toggle>
                        {(localStorage.getItem("orgLength") > 1 ||
                          isEnsarasAdmin) && (
                            <Dropdown.Item
                              onClick={() => {
                                navigate("/dashboard");
                                localStorage.removeItem("activePath");
                                dispatch(setIsAIAgent(false));
                              }}
                            >
                              Organization List
                            </Dropdown.Item>
                          )}
                        <Dropdown.Item
                          onClick={() => handleTabChange("overview")}
                          className={activeKey === "overview" ? "active-tab" : ""}
                        >
                          Organization Overview
                        </Dropdown.Item>
                        {isAdmin && (
                          <>
                            <Dropdown.Item
                              onClick={() => handleTabChange("account")}
                              className={
                                activeKey === "account" ? "active-tab" : ""
                              }
                            >
                              Account Management
                            </Dropdown.Item>
                            {isAdmin && (
                              <Dropdown.Item
                                onClick={() => handleTabChange("alerts")}
                                className={
                                  activeKey === "alerts" ? "active-tab" : ""
                                }
                              >
                                Alert System
                              </Dropdown.Item>
                            )}
                            <Dropdown.Item
                              onClick={() => handleTabChange("masterData")}
                              className={
                                activeKey === "masterData" ? "active-tab" : ""
                              }
                            >
                              Master Data Management
                            </Dropdown.Item>
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
              </div>
            )}
            <div className="mobile-nav-left">
              {needBack ? (
                <span
                  onClick={(e) =>
                    handleNavWithGuard(() =>
                      onCustomBack ? onCustomBack(e) : navigate(backLink),
                    )
                  }
                  className="nav-back-button"
                  style={{ cursor: "pointer" }}
                >
                  <FaArrowLeft />
                </span>
              ) : (
                <div
                  className="nav-back-placeholder"
                  style={{ width: "24px" }}
                ></div>
              )}
            </div>
            <Navbar.Brand>
              <a
                href="/dashboard"
                className="logo"
                onClick={() => dispatch(setIsAIAgent(false))}
              >
                <img
                  src={logo1}
                  alt="Ensaras Logo"
                  className="logo mob-logo"
                  loading="lazy"
                />
              </a>
            </Navbar.Brand>
            <div className="mobile-nav-right">
              {isAuthenticated && <NetworkStatusPill iconOnly />}
              {isAuthenticated && (
                <div
                  onClick={() => toggleSidebar()}
                  className="custom-toggle-button"
                >
                  <FiAlignJustify />
                </div>
              )}
            </div>

            {isAuthenticated && (
              <Navbar.Offcanvas
                show={showSidebar}
                onHide={closeSidebar}
                id={`offcanvasNavbar-expand-${expand}`}
                aria-labelledby={`offcanvasNavbarLabel-expand-${expand}`}
                placement="end"
              >
                <Offcanvas.Header>
                  <Offcanvas.Title id={`offcanvasNavbarLabel-expand-${expand}`}>
                    Tools
                  </Offcanvas.Title>
                  <div onClick={closeSidebar} className="custom-close-button">
                    <FiAlignJustify />
                  </div>
                </Offcanvas.Header>
                <Offcanvas.Body>
                  <Nav
                    className="justify-content-end flex-grow-1 pe-3"
                    style={{ overflow: "hidden" }}
                  >
                    {isAIAgent && (
                      <button
                        onClick={() => {
                          closeSidebar();
                          navigate(`/ai-assistant/${organization}`);
                        }}
                        className="nav-btn"
                      >
                        AI Assistant
                      </button>
                    )}
                    {organization && (
                      <button
                        onClick={() => {
                          closeSidebar();
                          navigate(`/documents/${organization}`);
                        }}
                        className={`nav-btn ${location.pathname.includes('/documents') ? 'active-nav-btn' : ''}`}
                      >
                        Documents
                      </button>
                    )}

                    <a
                      href="mailto:support@ensaras.com?subject=Incident:&body=%0AIncident%20Description:%0A%0A"
                      className="nav-btn"
                    >
                      Support
                    </a>
                    <button
                      className="nav-btn"
                      onClick={() => handleResetPasswordModal(true)}
                    >
                      Reset Password
                    </button>
                    <button onClick={handleLogout} className="nav-btn">
                      Log out
                    </button>
                  </Nav>
                </Offcanvas.Body>
                <MobileFooter />
              </Navbar.Offcanvas>
            )}
          </Container>
        </Navbar>

        <ResetPasswordModal
          show={showResetPasswordModal}
          onHide={() => handleResetPasswordModal(false)}
          updateMessage={updateMessage}
        />
      </div>
    );
  }
}

export default AppNavBar;
